import { useState, useEffect } from 'react';
import LandingPage from '../components/LandingPage';
import { detectLocale } from '../locales/config';

const DUTCH_LOCALES = ['nl-nl', 'fr-be'];

const faqItems = [
  {
    q: "Hoe werkt de schulden aflossen calculator?",
    a: "Voer elke schuld in met het openstaande saldo, het jaarlijkse rentepercentage en de maandelijkse minimale betaling. De schulden calculator past de sneeuwbal- of lawinemethode toe en laat je precies zien wanneer je schuldenvrij bent, hoeveel rente je in totaal betaalt en hoeveel je bespaart door elke maand iets extra af te lossen.",
  },
  {
    q: "Wat is beter: sneeuwbal- of lawinemethode?",
    a: "Dat hangt af van jouw situatie. De lawinemethode (eerst de schuld met de hoogste rente aflossen) bespaart het meest op rentekosten. De sneeuwbalmethode (eerst de kleinste schuld aflossen) geeft snelle successen die je motivatie hooghouden. Beide zijn veel effectiever dan alleen de minimale betaling doen.",
  },
  {
    q: "Hoe lang duurt het om mijn schulden af te lossen?",
    a: "Dat hangt af van je saldo, rentepercentage en maandelijkse betaling. Als je alleen het minimum betaalt op een creditcard, kan het meer dan 10 jaar duren om schuldenvrij te worden. Met een extra maandelijks bedrag kun je dit in 2 tot 3 jaar bereiken en duizenden euros aan rente besparen. Gebruik onze schulden aflossen calculator om je persoonlijke aflossingsplan te berekenen.",
  },
  {
    q: "Is deze calculator gratis?",
    a: "De schulden calculator is volledig gratis en vereist geen registratie. Je kunt je aflossingsplan voor leningen in seconden opstellen. Als je je persoonlijke plan wilt opslaan en downloaden als PDF, is er een eenmalige betaling zonder abonnement beschikbaar.",
  },
  {
    q: "Werkt het voor creditcards en leningen?",
    a: "Ja. Deze lening aflossen calculator werkt voor creditcards, persoonlijke leningen, autoleningen, studieleningen en elk type schuld met rente. Je kunt meerdere schulden tegelijk invoeren om de totale tijd te berekenen totdat je schuldenvrij bent.",
  },
];

const COLORS = { text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0" };

const sections = [
  {
    heading: 'Zo gebruik je deze schulden datum berekenen tool',
    body: 'Voer elke schuld in: huidig saldo, jaarlijks rentepercentage en maandelijkse minimale betaling. Voeg een extra bedrag toe dat je kunt gebruiken voor sneller schulden aflossen. De schulden calculator toont je aflossingsvrije datum, totale rentekosten en hoe elke strategie — sneeuwbal of lawine — je helpt om sneller schuldenvrij te worden.',
  },
  {
    heading: 'Sneeuwbal- vs. lawinemethode: welke kies je?',
    body: (
      <>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 12 }}>
          De <strong style={{ color: COLORS.text }}>sneeuwbalmethode</strong> lost eerst de kleinste schuld af
          en gebruikt die vrijgekomen betaling voor de volgende. Ideaal als je snelle successen nodig hebt
          om gemotiveerd te blijven bij het aflossen van leningen.
        </p>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 0 }}>
          De <strong style={{ color: COLORS.text }}>lawinemethode</strong> pakt eerst de schuld met de
          hoogste rente aan. Wiskundig gezien is dit de efficiëntste aanpak en bespaar je het meest.
          Gebruik onze schulden calculator om te zien hoeveel je bespaart met elke methode en hoe je
          eerder schuldenvrij kunt worden.
        </p>
      </>
    ),
  },
  {
    heading: 'Waarom minimale betalingen je jaren kosten',
    body: 'Creditcardrente wordt elke maand berekend over het openstaande saldo. Als je alleen het minimum betaalt, kan het meer dan 8–10 jaar duren voordat je een gemiddelde schuld hebt afgelost. Al 10–15 % meer dan de minimale betaling kan die tijd halveren en je duizenden euros besparen. Gebruik de lening aflossen calculator om de exacte impact van elk extra euro te zien in je persoonlijke aflossingsplan lening.',
  },
];

export default function SchuldenAflossen() {
  const [locale, setLocale] = useState('nl-nl');

  useEffect(() => {
    detectLocale().then(detected => {
      setLocale(DUTCH_LOCALES.includes(detected) ? detected : 'nl-nl');
    });
  }, []);

  return (
    <LandingPage
      locale={locale}
      pageUrl="https://clearmydebt.app/schulden-aflossen/"
      helmet={{
        lang: 'nl',
        title: 'Schulden Aflossen Calculator — Schuldenvrij Worden | ClearMyDebt',
        description: 'Bereken wanneer je schuldenvrij bent. Sneeuwbal- en lawinemethode. Zonder registratie. Direct resultaat.',
        ogTitle: 'Schulden Aflossen Calculator — Schuldenvrij Worden | ClearMyDebt',
        ogDescription: 'Bereken wanneer je schuldenvrij bent. Sneeuwbal- en lawinemethode. Zonder registratie. Direct resultaat.',
      }}
      hreflangList={[
        { hreflang: 'nl-nl', href: 'https://clearmydebt.app/schulden-aflossen/' },
        { hreflang: 'nl-be', href: 'https://clearmydebt.app/schulden-aflossen/' },
        { hreflang: 'x-default', href: 'https://clearmydebt.app/schulden-aflossen/' },
      ]}
      h1="Schulden Aflossen Calculator"
      intro="Voer je schulden in en ontdek precies wanneer je schuldenvrij bent — en hoeveel je bespaart met het juiste aflossingsplan."
      sections={sections}
      faqItems={faqItems}
      faqHeading="Veelgestelde vragen"
      moreHeading="Meer calculators"
      moreLinks={[
        { href: '/nl-nl/credit-card-payoff-calculator/', label: 'Creditcard calculator' },
        { href: '/nl-nl/debt-snowball-calculator/', label: 'Sneeuwbal methode' },
        { href: '/nl-nl/debt-avalanche-calculator/', label: 'Lawine methode' },
        { href: '/nl-nl/debt-repayment-calculator/', label: 'Aflossings calculator' },
      ]}
    />
  );
}
